import { useEffect, useState, useCallback } from "react";
import axiosInstance, { TokenManager } from "./utils/axiosConfig";
import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect
} from "react-router-dom";
import { Dashboard } from "./Dashboard";
import Hobbies from "./Profile/Hobbies";
import MessengerPage from "./Profile/MessengerPage";
// Conditionally import UnifiedLogin only for non-production environments
import SelectChildUser from "./Modals/SelectChildUser";
import Profile from "./Profile/Profile";
import Logout from "./Logout";
import ProductionRegistration from "./Modals/ProductionRegistration";
import ResetPasswordRequest from "./Modals/ResetPasswordRequest";
import ConfirmPasswordReset from "./Modals/ConfirmPasswordReset";

const { NODE_ENV, REACT_APP_ENVIRONMENT } = process.env;
// Use custom environment variable if set, otherwise fall back to NODE_ENV
const ENVIRONMENT = REACT_APP_ENVIRONMENT || NODE_ENV;
const IS_PRODUCTION = ENVIRONMENT?.toLowerCase() === 'production';

console.log("NODE_ENV:", NODE_ENV);
console.log("REACT_APP_ENVIRONMENT:", REACT_APP_ENVIRONMENT);
console.log("Using ENVIRONMENT:", ENVIRONMENT);
console.log("IS_PRODUCTION:", IS_PRODUCTION);

// Conditionally import UnifiedLogin component ONLY for non-production
let UnifiedLogin = null;

if (!IS_PRODUCTION) {
    try {
        UnifiedLogin = require("./Modals/UnifiedLogin").default;
        console.log("UnifiedLogin component loaded for non-production environment");
    } catch (error) {
        console.error("Failed to load UnifiedLogin:", error);
    }
} else {
    console.log("UnifiedLogin component completely excluded from production build");
}

const LoadingScreen = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw'
    }}>
        <div>
            {/* <h2>Loading...</h2> */}
            {/* You can add a spinner or progress indicator here */}
            <div className="spinner" id="spinner"></div>
        </div>
    </div>
);

const useAuthRecovery = (setUser) => {
    const [isRecovering, setIsRecovering] = useState(false);
    
    const attemptRecovery = useCallback(async () => {
        // If there's already a user in localStorage, don't attempt recovery
        // This allows the main useAuth() hook in App.jsx to handle localStorage authentication
        if (window.localStorage.getItem("USER")) {
            return;
        }
        
        setIsRecovering(true);
        
        try {
            // Check URL parameters
            const params = new URLSearchParams(window.location.search);
            const urlToken = params.get('token');
            
            // If we have a token in the URL, try to use it
            if (urlToken) {
                console.log("Found token in URL, attempting to validate");
                
                // Set token and attempt to get user data
                TokenManager.setToken(urlToken);
                
                try {
                    const response = await axiosInstance.get(`/auth/me`);
                    
                    if (response.data) {
                        console.log("Token validation successful");
                        
                        // Store user data and let App.jsx handle processing
                        // This prevents dual processing and maintains consistency
                        window.localStorage.setItem("USER", JSON.stringify(response.data));
                        if (response.data.settings) {
                            window.localStorage.setItem("SETTINGS", JSON.stringify(response.data.settings));
                        }
                        if (response.data.children) {
                            window.localStorage.setItem("CHILDREN", JSON.stringify(response.data.children));
                        }
                        
                        // Update app state - let App.jsx process it through normal flow
                        setUser(response.data);
                        return; // Recovery successful
                    }
                } catch (error) {
                    console.error("Token validation failed:", error);
                    // Continue to credential-based recovery
                }
            }
            
            // If token validation failed or no token found, check for credentials
            const username = params.get('username');
            const password = params.get('password');
            const email = params.get('email');
            const code = params.get('code');
            
            // Try kid login
            if (username && password) {
                console.log("Found credentials in URL, attempting login");
                
                try {
                    const response = await axiosInstance.post(`/auth/login`, {
                        username: username,
                        password: password
                    });
                    
                    if (response.data && response.data.access_token) {
                        console.log("Login successful using URL credentials");
                        
                        // Set token and save user data - let App.jsx handle processing
                        const token = response.data.access_token;
                        TokenManager.setToken(token);
                        
                        // Store user data and let App.jsx handle processing  
                        // This prevents dual processing and maintains consistency
                        window.localStorage.setItem("USER", JSON.stringify(response.data));
                        if (response.data.settings) {
                            window.localStorage.setItem("SETTINGS", JSON.stringify(response.data.settings));
                        }
                        if (response.data.children) {
                            window.localStorage.setItem("CHILDREN", JSON.stringify(response.data.children));
                        }
                        
                        // Update app state - let App.jsx process it through normal flow
                        setUser(response.data);
                        
                        // Clean URL to remove credentials for security
                        if (window.history.replaceState) {
                            const cleanUrl = window.location.pathname;
                            window.history.replaceState({}, document.title, cleanUrl);
                        }
                    }
                } catch (error) {
                    console.error("Login recovery failed:", error);
                }
            }
            // Try parent login
            else if (email && code) {
                console.log("Found parent credentials in URL, attempting login");
                
                try {
                    const response = await axiosInstance.post(`/auth/login-parent`, {
                        email: email,
                        code: code
                    });
                    
                    if (response.data && response.data.access_token) {
                        console.log("Parent login successful using URL credentials");
                        
                        // Set token and save user data
                        const token = response.data.access_token;
                        TokenManager.setToken(token);
                        
                        // Fetch user data using the token
                        const userResponse = await axiosInstance.get(`/auth/me`);
                        
                        if (userResponse.data) {
                            // Store user data and let App.jsx handle processing
                            // This prevents dual processing and maintains consistency
                            window.localStorage.setItem("USER", JSON.stringify(userResponse.data));
                            if (userResponse.data.settings) {
                                window.localStorage.setItem("SETTINGS", JSON.stringify(userResponse.data.settings));
                            }
                            if (userResponse.data.children) {
                                window.localStorage.setItem("CHILDREN", JSON.stringify(userResponse.data.children));
                            }
                            
                            // Update app state - let App.jsx process it through normal flow
                            setUser(userResponse.data);
                        }
                        
                        // Clean URL to remove credentials for security
                        if (window.history.replaceState) {
                            const cleanUrl = window.location.pathname;
                            window.history.replaceState({}, document.title, cleanUrl);
                        }
                    }
                } catch (error) {
                    console.error("Parent login recovery failed:", error);
                }
            }
        } catch (error) {
            console.error("Recovery process error:", error);
        } finally {
            setIsRecovering(false);
        }
    }, [setUser]);
    
    
    useEffect(() => {
        attemptRecovery();
    }, [attemptRecovery]);
    
    return { isRecovering };
};

const PrivateRoute = ({ children, user, isRecovering, isLoading, isDataFetched, ...rest }) => {
    return (
        <Route
            {...rest}
            render={({ location }) => {
                // Show loading during any authentication process
                if (isRecovering || isLoading || !isDataFetched) {
                    return <LoadingScreen />;
                }
                
                // After main authentication is complete, check if user exists
                if (user) {
                    return children;
                }
                
                // Handle unauthenticated users based on environment
                if (!IS_PRODUCTION) {
                    console.log(`PrivateRoute: ${ENVIRONMENT} environment - redirecting to UnifiedLogin`);
                    return (
                        <Redirect
                            to={{
                                pathname: "/",
                                state: { from: location }
                            }}
                        />
                    );
                } else {
                    console.log("PrivateRoute: Production environment - redirecting to WIX website");
                    // Immediate redirect without any delay
                    if (typeof window !== 'undefined') {
                        window.location.replace('https://www.bridgepalkids.com/');
                    }
                    return <LoadingScreen />; // Show loading while redirecting
                }
            }}
        />
    );
};

export default function Routes(props) {
    const {
        showNotification,
        user,
        setUser,
        theme,
        kids,
        proceedWithChild,
        selectChild,
        isLoading,
        setIsLoading,
        isParent,
        isMentor,
        isDataFetched
    } = props;
    
    // Use our recovery hook (must be called before any conditional returns)
    const { isRecovering } = useAuthRecovery(setUser);
    
    // PRIORITY CHECK: Password reset routes should render immediately without any auth checks
    // This must come BEFORE any loading/authentication checks
    const currentPath = window.location.pathname;
    const isPasswordResetRoute = currentPath === '/reset-password' || currentPath === '/reset-password/confirm';
    
    if (isPasswordResetRoute) {
        return (
            <Router>
                <Switch>
                    <Route exact path="/reset-password">
                        <ResetPasswordRequest />
                    </Route>
                    <Route exact path="/reset-password/confirm">
                        <ConfirmPasswordReset />
                    </Route>
                </Switch>
            </Router>
        );
    }
    
    // Add a combined loading state that considers both the recovery process and main auth process
    const isAuthenticating = isRecovering || isLoading || !isDataFetched;
    
    // Show a loading screen instead of immediately rendering the login page
    if (isAuthenticating) {
        return <LoadingScreen />;
    }
    
    // If user is not authenticated after main auth and recovery complete, handle based on environment
    if (!user && isDataFetched && !isLoading) {
        
        // Check if this is a registration attempt before redirecting
        const urlParams = new URLSearchParams(window.location.search);
        const isRegistrationAttempt = urlParams.get('register-new-user') === 'true';
        
        if (!IS_PRODUCTION && UnifiedLogin) {
            console.log(`${ENVIRONMENT} environment: Showing UnifiedLogin for local/staging testing`);
            return (
                <Router>
                    <Switch>
                        <Route exact path="/">
                            <UnifiedLogin user={user} setUser={setUser} />
                        </Route>
                        <Route path="*">
                            <UnifiedLogin user={user} setUser={setUser} />
                        </Route>
                    </Switch>
                </Router>
            );
        } else if (IS_PRODUCTION && isRegistrationAttempt) {
            // Use production-only registration component
            console.log("Production environment: Loading ProductionRegistration component");
            return (
                <Router>
                    <Switch>
                        <Route exact path="/">
                            <ProductionRegistration user={user} setUser={setUser} />
                        </Route>
                        <Route path="*">
                            <ProductionRegistration user={user} setUser={setUser} />
                        </Route>
                    </Switch>
                </Router>
            );
        } else {
            console.log("Production environment: Redirecting unauthenticated user to WIX website");
            // Immediate redirect without any delay - use replace to prevent back button issues
            if (typeof window !== 'undefined') {
                window.location.replace('https://www.bridgepalkids.com/');
            }
            return <LoadingScreen />; // Show loading while redirecting
        }
    }
    
    // The rest of your routing logic...
    if (isParent && !isMentor && selectChild) {
        return (
            <Router>
                <SelectChildUser
                    user={user}
                    isParent={isParent}
                    isMentor={isMentor}
                    kids={kids}
                    proceedWithChild={proceedWithChild}
                />
            </Router>
        );
    }
    
    return (
        <Router>
            <Switch>
                {/* Public password reset routes (no authentication required) */}
                <Route exact path="/reset-password">
                    <ResetPasswordRequest />
                </Route>
                
                <Route exact path="/reset-password/confirm">
                    <ConfirmPasswordReset />
                </Route>
                
                <PrivateRoute 
                    user={user} 
                    isRecovering={isRecovering} 
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    exact path="/"
                >
                    <Redirect to="/dashboard/home" />
                </PrivateRoute>
                <PrivateRoute
                    user={user}
                    isRecovering={isRecovering}
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    path="/select-kid"
                >
                    <SelectChildUser
                        user={user}
                        isParent={isParent}
                        isMentor={isMentor}
                        kids={kids}
                        proceedWithChild={proceedWithChild}
                        theme={theme}  // Add theme prop
                    />
                </PrivateRoute>
                <PrivateRoute 
                    user={user} 
                    isRecovering={isRecovering} 
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    path="/dashboard"
                >
                    <Dashboard
                        user={user}
                        isParent={isParent}
                        isMentor={isMentor}
                        showNotification={showNotification}
                        theme={theme}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                    >
                        <Switch>
                            <Route path="/dashboard/home" />
                            <Route path="/dashboard/life_topics" />
                            <Route path="/dashboard/xc_pals" />
                            <Route path="/dashboard/journal" />
                            <Route path="/dashboard/resources" />
                            <Route path="/dashboard/parents" />
                            <Redirect from="/dashboard" to="/dashboard/home" />
                        </Switch>
                    </Dashboard>
                </PrivateRoute>
                <PrivateRoute 
                    user={user} 
                    isRecovering={isRecovering} 
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    path="/hobbies"
                >
                    <Hobbies
                        user={user}
                        theme={theme}
                        showNotification={showNotification}
                    />
                </PrivateRoute>
                <PrivateRoute 
                    user={user} 
                    isRecovering={isRecovering} 
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    path="/messenger"
                >
                    <MessengerPage
                        user={user}
                        isParent={isParent}
                        isMentor={isMentor}
                        theme={theme}
                        showNotification={showNotification}
                    />
                </PrivateRoute>
                <PrivateRoute 
                    user={user} 
                    isRecovering={isRecovering} 
                    isLoading={isLoading}
                    isDataFetched={isDataFetched}
                    path="/user/:userId"
                >
                    <Profile
                        user={user}
                        isParent={isParent}
                        isMentor={isMentor}
                        theme={theme}
                        showNotification={showNotification}
                    />
                </PrivateRoute>
                <Route path="/logout">
                    <Logout />
                </Route>
                <Redirect to="/dashboard" />
            </Switch>
        </Router>
    );
}